import { appConstants } from './appConstants';
import { bffRoutes } from './configuration';
import { OK, CREATED, INTERNAL_SERVER_ERROR } from 'http-status-codes';
import { checkStatus } from './authFunctions';

export const getToken = (businessUnit, formName, flow, setIsLoading, setIsError, setIsSubmitted, lang, setPdfUrl) =>
  getJwtToken(businessUnit, formName, flow, setIsError).then((responseData) => {
    responseData &&
      responseData.token &&
      responseData.correlationId &&
      loadForm(
        responseData.token,
        responseData.correlationId,
        businessUnit,
        formName,
        flow,
        setIsLoading,
        setIsError,
        setIsSubmitted,
        lang,
        setPdfUrl,
      );
  });

const loadForm = (
  secret,
  correlationId,
  businessUnit,
  formName,
  flow,
  setIsLoading,
  setIsError,
  setIsSubmitted,
  lang,
  setPdfUrl,
) => {
  const language = lang === 'fr-CA' ? 'fr' : 'en';
  window.formhero.setFormHeroHost(appConstants.FORMHERO_HOST);
  window.formhero.useHttps(true);
  let statusUpdateResp = null;
  window.CWC_MLCA &&
    window.CWC_MLCA.dataLayer.push({
      event: 'form-initiated',
      form: {
        name: formName,
        language: language,
        id: correlationId.split(' ')[1],
        team: businessUnit,
      },
    });
  return window.formhero
    .loadForm(
      {
        onStatusFn(status) {
          if (flow === appConstants.FLOW_AUTH) {
            statusUpdate(status, setIsError, flow, formName);
          }

          if (flow === appConstants.FLOW_UNAUTH && status.state === 'submitted' && status.formHeroSessionId) {
            statusUpdateResp = { status, setIsError, flow };
          }
          //this will fired only when form is loaded on the screen and progress completion is not a type [Number]
          if (status.progress.completionPercentage === undefined) {
            setIsLoading(false);
          }
        },
        signedRequest: secret,
        lang: language,
      },
      {
        'FORMHERO.correlationId': correlationId.split(' ')[1],
      },
    )
    .then(async function (successResult) {
      if (null !== statusUpdateResp) {
        await statusUpdate(statusUpdateResp.status, statusUpdateResp.setIsError, statusUpdateResp.flow, formName);
      }
      window.CWC_MLCA &&
        window.CWC_MLCA.dataLayer.push({
          event: 'form-submit-success',
          form: {
            name: formName,
            language: language,
            id: correlationId.split(' ')[1],
            team: businessUnit,
          },
        });
      setIsLoading(false);
      setIsSubmitted(true);

      const status = await documentRetrieve(successResult, flow, setIsError, setPdfUrl, formName);

      setIsError(status !== OK);
    })
    .catch(() => {
      setIsError(true);
    });
};

const getJwtToken = (businessUnit, formName, flow, setIsError) => {
  const requestBody = JSON.stringify({
    businessUnit,
    formName,
    flow,
  });
  return fetch(bffRoutes.getTokenGenerate(), {
    mode: 'cors',
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: requestBody,
  })
    .then(checkStatus)
    .then((response) => response.json())
    .then((responseData) => responseData)
    .catch(() => {
      setIsError(true);
    });
};

/**
 * Sends request to save form data in db
 * @param {Object} formData collected data from daylight form
 * @param {Function} setIsError function to update isError state
 * @param {String} flow authorized or unauthorized
 * @param {String} formName form name from the URL
 * @returns Success or null
 */
const updateData = async (formData, setIsError, flow, formName) => {
  const requestBody = JSON.stringify({
    formData,
    flow,
    formName,
  });
  return fetch(bffRoutes.getTokenUpdate(), {
    credentials: 'include',
    mode: 'cors',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: requestBody,
  })
    .then(checkStatus)
    .then((response) => {
      if (response.status === OK || response.status === CREATED) {
        return 'Success';
      } else {
        setIsError(true);
        return null;
      }
    })
    .catch(() => {
      setIsError(true);
    });
};

export const statusUpdate = (status, setIsError, flow, formName) => {
  if (status && status.formHeroSessionId) {
    return updateData(status.collectedData, setIsError, flow, formName);
  }
  return null;
};

/**
 * Retrieves the submitted form documents and send request to save documents in DB
 * @param {Object} successResult
 * @param {String} flow
 * @param {Function} setIsError
 * @param {Function} setPdfUrl
 * @param {String} formName
 * @returns
 */
export const documentRetrieve = async (successResult, flow, setIsError, setPdfUrl, formName) => {
  let uploads = [];
  if (successResult.state === 'form-success') {
    uploads = uploads.concat(
      successResult.artifacts.map(async (item) => {
        if (
          item.fileName.startsWith(bffRoutes.getPdfNameCheckEnglish()) ||
          item.fileName.startsWith(bffRoutes.getPdfNameCheckFrench())
        ) {
          setPdfUrl(await getDocumentPdf(item.url));
        }
        return uploadDocuments(item.url, item.fileName, setIsError, item.type);
      }),
    );
  }
  const newV = await Promise.all(uploads);
  return saveDocuments(newV, flow, successResult.collectedData, setIsError, formName);
};

export const getDocumentPdf = async (url, setIsError) => {
  return fetch(url, {
    method: 'GET',
  })
    .then(checkStatus)
    .then(async (response) => {
      const body = await response.blob();
      const file = new Blob([body], { type: 'application/pdf' });

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        return file;
      } else {
        const fileURL = URL.createObjectURL(file);
        return fileURL;
      }
    })
    .catch(() => {
      setIsError(true);
    });
};

const uploadDocuments = async (url, fileName, setIsError, fileType) => {
  return fetch(url, {
    method: 'GET',
  })
    .then(checkStatus)
    .then(async (response) => {
      return {
        id: fileName,
        data: await blob2b64(await response.blob(), setIsError),
        fileType,
      };
    })
    .catch(() => {
      setIsError(true);
    });
};

export const blob2b64 = (blob, setIsError) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        reader.readAsArrayBuffer(blob);
        reader.onloadend = function () {
          let binary = '';
          const bytes = new Uint8Array(reader.result);
          const length = bytes.byteLength;
          for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64data = btoa(binary);
          resolve(base64data);
        };
      } else {
        reader.readAsBinaryString(blob);
        reader.onloadend = function () {
          const base64data = btoa(reader.result);
          resolve(base64data);
        };
      }
    } catch (e) {
      setIsError(true);
      reject(e);
    }
  });
};

const saveDocuments = async (uploads, flow, formData, setIsError, formName) => {
  const payload = JSON.stringify({
    uploads,
    flow,
    formData,
    formName,
  });
  return fetch(bffRoutes.getSaveDocuments(), {
    mode: 'cors',
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: payload,
  })
    .then(checkStatus)
    .then((response) => response.status)
    .catch(() => {
      setIsError(true);
      return INTERNAL_SERVER_ERROR;
    });
};
